import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MarketDepthDto, DepthLevelDto, TradeDto } from '../_core/_model/market';
import { MainCurrencyDto } from '../_core/_model/currency';
import { ApiService } from '../_core/_services/api.service';
import { OrderPrefill } from '../_core/_services/order-draft-service';
import { Chart, TooltipItem } from 'chart.js/auto';
import { CandlestickController, CandlestickElement } from 'chartjs-chart-financial';
import type { CandleDto } from '../_core/_model/market';
import { NotificationService } from '../_core/_services/notification.service';

@Component({
  selector: 'app-markets',
  standalone: false,
  templateUrl: './markets.html',
  styleUrl: './markets.css',
})
export class Markets implements OnInit {

  @ViewChild('priceChart') priceChartRef!: ElementRef<HTMLCanvasElement>;
  private chart: Chart | null = null;
  
  loading = false;
  error = '';

  currencies: MainCurrencyDto[] = [];

  fee: number = 0.0;

  // Market depth state
  marketLoading = false;
  marketError = '';

  depth = 20;
  group = 0.25;


  pairOptions: string[] = [];
  selectedPair = 'USD/NGN'; // default fallback

  depthOptions = [20, 50, 100];
  groupOptions = [0.25, 0.5, 1.0, 5.0];

  market: MarketDepthDto | null = null;
  asks: DepthLevelDto[] = [];
  bids: DepthLevelDto[] = [];
  trades: TradeDto[] = [];
  refreshedAt = new Date();

  selectedTimeframe: '1H' | '4H' | '1D' | '1W' = '1H';
  candles: CandleDto[] = [];

  private timeframeConfig(tf: '1H' | '4H' | '1D' | '1W') {
    switch (tf) {
      case '1H':
        return { apiTf: '1m', limit: 60 };     // 60 mins
      case '4H':
        return { apiTf: '5m', limit: 48 };     // 4 hours (5m candles)
      case '1D':
        return { apiTf: '15m', limit: 96 };    // 24 hours (15m candles)
      case '1W':
        return { apiTf: '1h', limit: 168 };    // 7 days (hour candles)
      default:
        return { apiTf: '1m', limit: 60 };
    }
  }

  quickTradeDraft: OrderPrefill | null = null;

  constructor(
    private notifyService: NotificationService,
    private api: ApiService
  ) {
    Chart.register(CandlestickController, CandlestickElement);
  }




  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }
  
  loadCandles() {
    const { base, quote } = this.parsePair(this.selectedPair);
    const { apiTf, limit } = this.timeframeConfig(this.selectedTimeframe);

    this.api.getCandles(base, quote, apiTf, limit).subscribe({
      next: (res) => {
        this.candles = res.data?.candles ?? [];
        this.renderCandleChart();
      },
      error: () => {
        this.candles = [];
        this.renderCandleChart();
      }
    });
  }

  setTimeframe(tf: '1H' | '4H' | '1D' | '1W') {
    if (this.selectedTimeframe === tf) return;
    this.selectedTimeframe = tf;
    this.loadCandles();
  }

  private renderCandleChart() {
    const el = this.priceChartRef?.nativeElement;
    if (!el) return;

    const data = (this.candles ?? []).map((c: any) => ({
      x: new Date(c.time),   // time adapter handles Date
      o: c.open,
      h: c.high,
      l: c.low,
      c: c.close,
    }));

    this.chart?.destroy();

    this.chart = new Chart(el, {
      type: 'candlestick' as any,
      data: {
        datasets: [
          {
            label: this.selectedPair,
            data: data as any,

            // --- Binance / TradingView style ---
            borderWidth: 1,
            barThickness: 'flex',
            categoryPercentage: 0.8,

            // chartjs-chart-financial supports this "color" object
            // (If your version doesn’t, see fallback notes below)
            color: {
              up: '#0ECB81',        // TradingView/Binance green
              down: '#F6465D',      // TradingView/Binance red
              unchanged: '#999999',
            },

            // wick color + border colors (some versions read these)
            borderColor: '#2b2f36',
            wickColor: '#2b2f36',
          } as any,
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,

        layout: { padding: { left: 6, right: 10, top: 8, bottom: 0 } },

        interaction: {
          mode: 'nearest',
          intersect: false,
          axis: 'x',
        },

        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              title: (items: TooltipItem<'candlestick'>[]) => {
                const raw: any = items?.[0]?.raw;
                const t = raw?.x ? new Date(raw.x) : null;
                return t ? t.toLocaleString() : '';
              },
              label: (item: TooltipItem<'candlestick'>) => {
                const v: any = item.raw;
                if (!v) return '';
                return `O ${Number(v.o).toFixed(2)}  H ${Number(v.h).toFixed(2)}  L ${Number(v.l).toFixed(2)}  C ${Number(v.c).toFixed(2)}`;
              },
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            grid: {
              color: 'rgba(0,0,0,0.06)',
              drawBorder: false,
            },
            ticks: {
              maxTicksLimit: 6,
              color: 'rgba(0,0,0,0.55)',
              font: { size: 11 },
            },
          },
          y: {
            position: 'right',
            grid: {
              color: 'rgba(0,0,0,0.06)',
              drawBorder: false,
            },
            ticks: {
              maxTicksLimit: 5,
              color: 'rgba(0,0,0,0.55)',
              font: { size: 11 },
              callback: (v: string | number) => Number(v).toLocaleString(),
            },
          },
        },
      },
    });
  }

  ngOnInit(): void {
    this.loadMain();
  }


  loadMain(): void {
    this.loading = true;

    this.api.getMain().subscribe({
      next: response => {
        this.currencies = (response.data?.currencies ?? [])
          .filter(currency => currency.status === 'Active');

        this.pairOptions = this.buildPairOptions(false);
        if (!this.pairOptions.includes(this.selectedPair)) {
          this.selectedPair = this.pairOptions[0] ?? 'USD/NGN';
        }

        this.setQuickTradePair();
        this.loadMarket();
        this.loadCandles();
      },
      error: error => {
        this.error = error?.error?.Error?.Details ?? 'Failed to load main data';
        this.notifyService.showError(this.error, 'Error');
      },
      complete: () => (this.loading = false),
    });
  }




  private parsePair(pair: string) {
    const [base, quote] = pair.split('/');
    return { base, quote };
  }

  loadMarket() {
    this.marketLoading = true;
    this.marketError = '';

    const { base, quote } = this.parsePair(this.selectedPair);

    this.api.getMarketDepth(base, quote, this.depth, this.group).subscribe({
      next: (res) => {
        this.market = res.data;
        this.asks = this.market?.asks ?? [];
        this.bids = this.market?.bids ?? [];
        this.trades = this.market?.trades ?? [];
        this.refreshedAt = new Date();

        setTimeout(() => this.loadCandles()); // ensures canvas exists
      },
      error: (e) => (this.marketError = e?.error?.Error?.Details ?? e?.message ?? 'Failed to load market depth'),
      complete: () => (this.marketLoading = false),
    });
  }

  refreshMarket() {
    this.loadMarket();
  }

  askBarWidth(row: any): string {
    const max = this.asks.length ? this.asks[this.asks.length - 1].total : 0;
    if (!max) return '0%';
    return `${Math.min(100, (row.total / max) * 100)}%`;
  }

  bidBarWidth(row: any): string {
    const max = this.bids.length ? this.bids[this.bids.length - 1].total : 0;
    if (!max) return '0%';
    return `${Math.min(100, (row.total / max) * 100)}%`;
  }

  changeClass(v: number | null | undefined): string {
    if (v == null) return 'text-muted';
    if (v > 0) return 'text-success';
    if (v < 0) return 'text-danger';
    return 'text-muted';
  }

  tradePriceClass(side: string): string {
    return side === 'Buy' ? 'text-success fw-semibold' : 'text-danger fw-semibold';
  }

  private buildPairOptions(includeReverse = false): string[] {
    const active = (this.currencies ?? [])
      .filter(c => c.status === 'Active')
      .map(c => c.currencyCode)
      .filter(Boolean);

    // unique + stable order
    const codes = Array.from(new Set(active)).sort();

    const pairs: string[] = [];

    for (let i = 0; i < codes.length; i++) {
      for (let j = i + 1; j < codes.length; j++) {
        const a = codes[i];
        const b = codes[j];

        pairs.push(`${a}/${b}`);

        if (includeReverse) {
          pairs.push(`${b}/${a}`);
        }
      }
    }

    return pairs;
  }

  onPairChanged(): void {
    this.setQuickTradePair();
    this.loadMarket();
    this.loadCandles();
  }

  private setQuickTradePair(): void {
    const { base, quote } = this.parsePair(this.selectedPair);
    this.quickTradeDraft = {
      haveCurrency: base,
      haveAmount: 0,
      needCurrency: quote,
      needAmount: 0,
      price: 0,
      executionMode: 'instant'
    };
  }
}
